import { useState, useMemo, useCallback, useEffect } from 'react';
import { Check, X, RotateCcw, ChevronDown, ChevronUp, Rows3, Columns2 } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { ActionButtonBase } from '../../common/ActionButtonBase';

export interface DiffChange {
  type: 'add' | 'remove' | 'unchanged';
  content: string;
  line_start: number;
  line_end: number;
}

interface LineDiff {
  id: string;
  type: 'add' | 'remove' | 'unchanged' | 'modify';
  oldContent?: string;
  newContent?: string;
  content?: string;
  lineNumber: number;
}

interface DiffViewProps {
  before: string;
  after: string;
  changes?: DiffChange[];
  htmlDiff?: string;
  onAccept: (result: string) => void;
  onReject: () => void;
  onEdit?: () => void;
  isLoading?: boolean;
  title?: string;
  warning?: string;
}

export function DiffView({
  before,
  after,
  changes = [],
  htmlDiff,
  onAccept,
  onReject,
  onEdit,
  isLoading = false,
  title = 'Modifications proposées',
  warning
}: DiffViewProps) {
  const { t } = useLanguage();
  const [viewMode, setViewMode] = useState<'unified' | 'split'>('unified');
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Compute line-by-line diff
  const lineDiffs = useMemo(() => {
    const beforeLines = before.split('\n');
    const afterLines = after.split('\n');
    const diffs: LineDiff[] = [];
    
    let i = 0, j = 0;
    let lineId = 0;
    
    // Simple LCS-based diff
    while (i < beforeLines.length || j < afterLines.length) {
      const beforeLine = beforeLines[i];
      const afterLine = afterLines[j];
      
      if (i >= beforeLines.length) {
        // Only additions left
        diffs.push({
          id: `diff-${lineId++}`,
          type: 'add',
          newContent: afterLine,
          lineNumber: j + 1
        });
        j++;
      } else if (j >= afterLines.length) {
        // Only removals left
        diffs.push({
          id: `diff-${lineId++}`,
          type: 'remove',
          oldContent: beforeLine,
          lineNumber: i + 1
        });
        i++;
      } else if (beforeLine === afterLine) {
        // Unchanged
        diffs.push({
          id: `diff-${lineId++}`,
          type: 'unchanged',
          content: beforeLine,
          lineNumber: i + 1
        });
        i++;
        j++;
      } else {
        // Check if this is a modification (similar content) or add/remove
        const beforeInAfter = afterLines.slice(j).findIndex(l => l === beforeLine);
        const afterInBefore = beforeLines.slice(i).findIndex(l => l === afterLine);
        
        if (beforeInAfter === -1 && afterInBefore === -1) {
          // It's a modification of the same line
          diffs.push({
            id: `diff-${lineId++}`,
            type: 'modify',
            oldContent: beforeLine,
            newContent: afterLine,
            lineNumber: i + 1
          });
          i++;
          j++;
        } else if (beforeInAfter !== -1 && (afterInBefore === -1 || beforeInAfter <= afterInBefore)) {
          // Line was added
          diffs.push({
            id: `diff-${lineId++}`,
            type: 'add',
            newContent: afterLine,
            lineNumber: j + 1
          });
          j++;
        } else {
          // Line was removed
          diffs.push({
            id: `diff-${lineId++}`,
            type: 'remove',
            oldContent: beforeLine,
            lineNumber: i + 1
          });
          i++;
        }
      }
    }
    
    return diffs;
  }, [before, after]);

  // Track which changes are accepted (default: all accepted)
  const [acceptedChanges, setAcceptedChanges] = useState<Set<string>>(new Set());

  // Initialize accepted changes when lineDiffs changes
  useEffect(() => {
    const initial = new Set<string>();
    lineDiffs.forEach(diff => {
      if (diff.type !== 'unchanged') {
        initial.add(diff.id);
      }
    });
    setAcceptedChanges(initial);
  }, [lineDiffs]);

  // Count stats
  const changeCount = lineDiffs.filter(d => d.type !== 'unchanged').length;
  const acceptedCount = acceptedChanges.size;

  // Toggle individual change
  const toggleChange = useCallback((id: string) => {
    setAcceptedChanges(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Compute final result based on accepted changes
  const computeFinalResult = useCallback(() => {
    const resultLines: string[] = [];
    
    lineDiffs.forEach(diff => {
      if (diff.type === 'unchanged') {
        resultLines.push(diff.content || '');
      } else if (diff.type === 'add') {
        if (acceptedChanges.has(diff.id)) {
          resultLines.push(diff.newContent || '');
        }
        // If not accepted, the added line is not included
      } else if (diff.type === 'remove') {
        if (!acceptedChanges.has(diff.id)) {
          // If rejection of removal = keep the old line
          resultLines.push(diff.oldContent || '');
        }
        // If accepted, the line is removed (not added to result)
      } else if (diff.type === 'modify') {
        if (acceptedChanges.has(diff.id)) {
          resultLines.push(diff.newContent || '');
        } else {
          resultLines.push(diff.oldContent || '');
        }
      }
    });
    
    return resultLines.join('\n');
  }, [lineDiffs, acceptedChanges]);

  // Handle final accept
  const handleAccept = () => {
    const result = computeFinalResult();
    onAccept(result);
  };

  // Render a single diff line with accept/reject controls
  const renderDiffLine = (diff: LineDiff, index: number) => {
    const isAccepted = acceptedChanges.has(diff.id);
    
    if (diff.type === 'unchanged') {
      return (
        <div key={diff.id} className="diff-line diff-unchanged">
          <span className="diff-marker"> </span>
          <span className="diff-content">{diff.content}</span>
          <span className="diff-actions" style={{ width: '60px' }}></span>
        </div>
      );
    }

    if (diff.type === 'modify') {
      return (
        <div key={diff.id} className="diff-block">
          <div className={`diff-line diff-removed ${!isAccepted ? 'diff-kept' : 'diff-will-remove'}`}>
            <span className="diff-marker">-</span>
            <span className="diff-content">{diff.oldContent}</span>
            <span className="diff-actions">
              <button
                onClick={() => toggleChange(diff.id)}
                className={`diff-action-btn ${isAccepted ? 'accepted' : 'rejected'}`}
                title={isAccepted ? 'Refuser ce changement' : 'Accepter ce changement'}
              >
                {isAccepted ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
              </button>
            </span>
          </div>
          <div className={`diff-line diff-added ${isAccepted ? 'diff-will-add' : 'diff-rejected'}`}>
            <span className="diff-marker">+</span>
            <span className="diff-content">{diff.newContent}</span>
            <span className="diff-actions"></span>
          </div>
        </div>
      );
    }

    if (diff.type === 'add') {
      return (
        <div key={diff.id} className={`diff-line diff-added ${isAccepted ? 'diff-will-add' : 'diff-rejected'}`}>
          <span className="diff-marker">+</span>
          <span className="diff-content">{diff.newContent}</span>
          <span className="diff-actions">
            <button
              onClick={() => toggleChange(diff.id)}
              className={`diff-action-btn ${isAccepted ? 'accepted' : 'rejected'}`}
              title={isAccepted ? 'Refuser cet ajout' : 'Accepter cet ajout'}
            >
              {isAccepted ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
            </button>
          </span>
        </div>
      );
    }

    if (diff.type === 'remove') {
      return (
        <div key={diff.id} className={`diff-line diff-removed ${isAccepted ? 'diff-will-remove' : 'diff-kept'}`}>
          <span className="diff-marker">-</span>
          <span className="diff-content">{diff.oldContent}</span>
          <span className="diff-actions">
            <button
              onClick={() => toggleChange(diff.id)}
              className={`diff-action-btn ${isAccepted ? 'accepted' : 'rejected'}`}
              title={isAccepted ? 'Annuler cette suppression' : 'Confirmer cette suppression'}
            >
              {isAccepted ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
            </button>
          </span>
        </div>
      );
    }

    return null;
  };

  return (
    <div 
      className="diff-view rounded-lg border overflow-hidden"
      style={{ 
        backgroundColor: 'var(--color-surface)',
        borderColor: 'var(--color-border)'
      }}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between p-3 gap-4 cursor-pointer"
        style={{ 
          backgroundColor: 'var(--color-surface-hover)',
          borderBottom: '1px solid var(--color-border)'
        }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center gap-3">
          <h3 className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>
            {title}
          </h3>
          <div className="flex items-center text-xs">
            <span 
              className="group/badge px-2 py-0.5 rounded-full font-semibold tabular-nums cursor-default"
              style={{
                backgroundColor: acceptedCount === changeCount ? 'rgba(34, 197, 94, 0.2)' : 'rgba(234, 179, 8, 0.2)',
                color: acceptedCount === changeCount ? 'var(--color-success)' : 'var(--color-warning)'
              }}
            >
              <span>{acceptedCount}/{changeCount}</span>
              <span className="hidden group-hover/badge:inline"> {t.positioning?.accepted || 'acceptés'}</span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* View mode toggle */}
          <div 
            className="flex rounded-lg overflow-hidden"
            style={{ border: '1px solid var(--color-border)' }}
          >
            <button
              onClick={(e) => { e.stopPropagation(); setViewMode('unified'); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors"
              style={viewMode === 'unified' 
                ? { backgroundColor: 'var(--color-primary)', color: 'white' } 
                : { backgroundColor: 'var(--color-surface)', color: 'var(--color-text-secondary)' }}
            >
              <Rows3 className="w-3 h-3" />
              {t.positioning?.unifiedView || 'Unifié'}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setViewMode('split'); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors"
              style={viewMode === 'split' 
                ? { backgroundColor: 'var(--color-primary)', color: 'white' } 
                : { backgroundColor: 'var(--color-surface)', color: 'var(--color-text-secondary)' }}
            >
              <Columns2 className="w-3 h-3" />
              {t.positioning?.splitView || 'Côte à côte'}
            </button>
          </div>
          {isCollapsed ? (
            <ChevronDown className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
          ) : (
            <ChevronUp className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
          )}
        </div>
      </div>

      {/* Diff content */}
      {!isCollapsed && (
        <>
          {/* Warning banner if present */}
          {warning && (
            <div 
              className="flex items-start gap-3 px-4 py-3"
              style={{ 
                backgroundColor: 'rgba(251, 191, 36, 0.15)',
                borderBottom: '1px solid rgba(251, 191, 36, 0.3)'
              }}
            >
              <span className="text-lg">⚠️</span>
              <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                {warning}
              </p>
            </div>
          )}
          
          {/* Help text (les actions globales « tout accepter / tout refuser » ont été
              retirées — l'accept/reject se fait ligne par ligne via les boutons ✓/✗). */}
          <div
            className="flex items-center px-4 py-2 text-xs"
            style={{
              backgroundColor: 'var(--color-surface)',
              borderBottom: '1px solid var(--color-border)'
            }}
          >
            <span style={{ color: 'var(--color-text-secondary)' }}>
              {t.positioning?.clickToReview || 'Cliquez sur ✓ ou ✗ pour chaque ligne'}
            </span>
          </div>

          <div
            className="diff-content-area overflow-auto max-h-96 p-4"
            style={{ fontFamily: 'monospace', fontSize: '12px' }}
          >
            {viewMode === 'unified' ? (
              // Unified view with per-line controls
              <div className="space-y-0">
                {lineDiffs.map((diff, index) => renderDiffLine(diff, index))}
              </div>
            ) : (
              // Split view
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div 
                    className="text-xs font-medium mb-2 px-2 py-1 rounded"
                    style={{ 
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      color: 'var(--color-text-secondary)'
                    }}
                  >
                    {t.positioning?.beforeLabel || 'Avant'}
                  </div>
                  <pre className="whitespace-pre-wrap text-xs" style={{ color: 'var(--color-text-primary)' }}>
                    {before}
                  </pre>
                </div>
                <div>
                  <div 
                    className="text-xs font-medium mb-2 px-2 py-1 rounded"
                    style={{ 
                      backgroundColor: 'rgba(34, 197, 94, 0.1)',
                      color: 'var(--color-text-secondary)'
                    }}
                  >
                    {t.positioning?.expectedResult || 'Résultat (prévu)'}
                  </div>
                  <pre className="whitespace-pre-wrap text-xs" style={{ color: 'var(--color-text-primary)' }}>
                    {computeFinalResult()}
                  </pre>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div 
            className="flex items-center justify-end gap-2 p-3"
            style={{ 
              borderTop: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-surface-hover)'
            }}
          >
            {onEdit && (
              <ActionButtonBase
                icon={RotateCcw}
                label={t.positioning?.modify || 'Modifier'}
                variant="neutral"
                appearance="outline"
                revealLabelOnHover={true}
                onClick={onEdit}
                disabled={isLoading}
                className="h-8"
              />
            )}
            <ActionButtonBase
              icon={X}
              label={t.positioning?.reject || 'Refuser'}
              variant="danger"
              appearance="outline"
              revealLabelOnHover={true}
              onClick={onReject}
              disabled={isLoading}
              className="h-8"
            />
            <ActionButtonBase
              icon={Check}
              label={t.positioning?.accept || 'Accepter'}
              variant="default"
              appearance="filled"
              revealLabelOnHover={true}
              onClick={handleAccept}
              disabled={isLoading || acceptedCount === 0}
              className="h-8"
            />
          </div>
        </>
      )}

      {/* CSS for diff styling */}
      <style>{`
        .diff-line {
          display: flex;
          padding: 2px 8px;
          font-family: monospace;
          font-size: 12px;
          line-height: 1.5;
          align-items: flex-start;
          transition: opacity 0.2s, background-color 0.2s;
        }
        
        .diff-block {
          border-left: 3px solid var(--color-border);
          margin-left: 4px;
          padding-left: 0;
        }
        
        .diff-marker {
          width: 16px;
          flex-shrink: 0;
          text-align: center;
          font-weight: bold;
          user-select: none;
        }
        
        .diff-content {
          flex: 1;
          white-space: pre-wrap;
          word-break: break-word;
        }
        
        .diff-actions {
          width: 32px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .diff-action-btn {
          width: 20px;
          height: 20px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          border: 1px solid transparent;
        }
        
        .diff-action-btn.accepted {
          background-color: rgba(34, 197, 94, 0.2);
          color: var(--color-success);
          border-color: var(--color-success);
        }

        .diff-action-btn.accepted:hover {
          background-color: rgba(34, 197, 94, 0.4);
        }

        .diff-action-btn.rejected {
          background-color: rgba(239, 68, 68, 0.2);
          color: var(--color-danger);
          border-color: var(--color-danger);
        }

        .diff-action-btn.rejected:hover {
          background-color: rgba(239, 68, 68, 0.4);
        }

        .diff-unchanged {
          color: var(--color-text-primary);
        }

        .diff-added {
          background-color: rgba(34, 197, 94, 0.15);
          color: var(--color-success);
        }

        .diff-added.diff-rejected {
          opacity: 0.4;
          text-decoration: line-through;
          background-color: rgba(34, 197, 94, 0.05);
        }

        .diff-added.diff-will-add {
          background-color: rgba(34, 197, 94, 0.2);
        }

        .diff-added .diff-marker {
          color: var(--color-success);
        }

        .diff-removed {
          background-color: rgba(239, 68, 68, 0.15);
          color: var(--color-danger);
          text-decoration: line-through;
        }

        .diff-removed.diff-kept {
          text-decoration: none;
          background-color: rgba(234, 179, 8, 0.15);
          color: var(--color-warning);
        }

        .diff-removed.diff-will-remove {
          background-color: rgba(239, 68, 68, 0.2);
        }

        .diff-removed .diff-marker {
          color: var(--color-danger);
        }

        .diff-removed.diff-kept .diff-marker {
          color: var(--color-warning);
        }

        @media (prefers-color-scheme: dark) {
          .diff-added {
            background-color: rgba(34, 197, 94, 0.2);
            color: var(--color-success);
          }

          .diff-added.diff-rejected {
            background-color: rgba(34, 197, 94, 0.05);
          }

          .diff-added .diff-marker {
            color: var(--color-success);
          }

          .diff-removed {
            background-color: rgba(239, 68, 68, 0.2);
            color: var(--color-danger);
          }

          .diff-removed.diff-kept {
            background-color: rgba(234, 179, 8, 0.2);
            color: var(--color-warning);
          }

          .diff-removed .diff-marker {
            color: var(--color-danger);
          }

          .diff-removed.diff-kept .diff-marker {
            color: var(--color-warning);
          }

          .diff-action-btn.accepted {
            background-color: rgba(34, 197, 94, 0.3);
            color: var(--color-success);
            border-color: var(--color-success);
          }

          .diff-action-btn.rejected {
            background-color: rgba(239, 68, 68, 0.3);
            color: var(--color-danger);
            border-color: var(--color-danger);
          }
        }
      `}</style>
    </div>
  );
}

export default DiffView;
